import api from '@/lib/api/axios'

export interface IconItem {
    id: number
    filename: string
    description: string | null
    tags: string | null
    /** System icon (blank frame / service fallback) — cannot be deleted. */
    reserved?: boolean
}

export interface IconUsage {
    iconId: number
    filename: string
    counts: { objects: number; runbooks: number; technologies: number; manufacturers: number }
    total: number
    inUse: boolean
    sampleObjects: { objectID: string; description: string | null; kind: string | null }[]
}

export const iconsApi = {
    list: (search?: string) =>
        api.get<{ success: boolean; data: IconItem[] }>('/api/icons', {
            params: search ? { search } : undefined,
        }),

    create: (data: { filename: string; description?: string; tags?: string; overwrite?: boolean }) =>
        api.post<{ success: boolean; data: IconItem }>('/api/icons', data),

    /** Where an icon is referenced (objects/services, runbooks, …) — drives the delete warning. */
    usage: (id: number) =>
        api.get<{ success: boolean; data: IconUsage }>(`/api/icons/${id}/usage`),

    /** Delete an icon: clears every reference in the base tables, then removes the icon + file. */
    remove: (id: number) =>
        api.delete<{ success: boolean; data: { id: number; filename: string; cleared: Record<string, number>; fileDeleted: boolean } }>(
            `/api/icons/${id}`,
        ),

    /** Upload icon file to image server, then create (or overwrite) the DB record */
    upload: async (file: File, description?: string, tags?: string, overwrite?: boolean): Promise<IconItem> => {
        // 1. Get a short-lived presigned URL and upload the file BYTES straight to
        //    S3. This bypasses CloudFront/WAF entirely — WAF body inspection was
        //    intermittently false-blocking binary PNGs (their bytes coincidentally
        //    matching an attack signature), which surfaced as an HTML
        //    "Unexpected token '<'" error on the previous /api/images/upload path.
        const contentType = file.type || 'application/octet-stream'
        const presign = await api.post<{ uploadUrl: string; key: string; url: string; cacheControl: string }>(
            '/api/images/presign',
            { directory: 'foreground/icons', imageName: file.name, contentType },
        )

        const putRes = await fetch(presign.data.uploadUrl, {
            method: 'PUT',
            body: file,
            // Cache-Control matters as much as Content-Type: the filename is the icon's identity,
            // so replacing one overwrites the same key and the URL is mutable. Sent from the
            // server's value so it cannot drift from what the backend stamps.
            headers: { 'Content-Type': contentType, 'Cache-Control': presign.data.cacheControl },
        })
        if (!putRes.ok) {
            throw new Error('Failed to upload icon file to storage')
        }

        // 2. Create (or overwrite) the DB record. A 409 here means a duplicate
        //    filename; the caller can retry with overwrite: true.
        const dbRes = await iconsApi.create({ filename: file.name, description, tags, overwrite })
        return dbRes.data.data
    },
}
